import { computeAnnualReport } from '../../utils/annualReport';
import { buildYearTenderStats } from './tenderReportBuilder';
import { buildContractAcceptanceStats, computeAvgContractValue } from './contractAcceptanceStats';
import { buildFleetYearReport } from './fleetReportBuilder';
import { buildYearDebrief } from './yearDebriefBuilder';

/**
 * Build the yearly closing report at end of fiscal year (360 days).
 * Returns `{ annualReport, updatedYearlyReports, patchedDrivers }`
 * where `patchedDrivers` is a NEW array with top-3 satisfaction bumped (+5).
 * Returns `null` if not on the 360-day boundary OR if there are no months
 * in the closing year (e.g. brand-new game).
 *
 * IMPORTANT: this helper MUTATES `driversAfterLease` references the same way
 * the inline code did (replacing entries in-place) so the array identity is
 * preserved for downstream code. Callers pass the array as `driversAfterLease`.
 */
export function buildAnnualReport({
  state,
  monthlyReport,
  updatedMonthlyHistory,
  newYear,
  driversAfterLease,
  finalVehicles,
  finalTreasury,
}) {
  if (!((state.totalDays + 1) % 360 === 0 && state.totalDays > 1)) return null;

  const closingYear = monthlyReport ? monthlyReport.year : newYear - 1;
  const yearMonths = updatedMonthlyHistory.filter(m => m.year === closingYear);
  if (yearMonths.length === 0) return null;

  const totalRevenue = yearMonths.reduce((s, m) => s + (m.totalRevenue || 0), 0);
  const totalExpenses = yearMonths.reduce((s, m) => s + (m.totalExpenses || 0), 0);
  const netResult = totalRevenue - totalExpenses;
  const bestMonth = yearMonths.reduce((a, b) => ((a.netResult ?? -Infinity) > (b.netResult ?? -Infinity) ? a : b));
  const worstMonth = yearMonths.reduce((a, b) => ((a.netResult ?? Infinity) < (b.netResult ?? Infinity) ? a : b));
  // Treasury delta: first record vs last record in the year (approximate, based on treasuryHistory).
  const firstTreasury = yearMonths[0]?.treasury ?? finalTreasury;
  const treasuryDelta = Math.round(finalTreasury) - Math.round(firstTreasury);

  // --- Évaluation annuelle des chauffeurs ---
  // 22/07/2026 (retour user « 0 trajets, aucun détail ») : stats annuelles
  // réelles (trajets, CA attribué, jours manqués) tracées quotidiennement
  // par applyDriverDailyActivity. Score basé sur l'année écoulée.
  const scoredDrivers = (driversAfterLease || []).map(d => ({
    id: d.id,
    name: d.name,
    level: d.level,
    competence: d.competence,
    satisfaction: d.satisfaction,
    xp: d.xp,
    totalTrips: d.totalTrips || 0,
    yearTrips: d.yearTrips || 0,
    yearRevenue: Math.round(d.yearRevenue || 0),
    yearMissedDays: d.yearMissedDays || 0,
    score: (d.competence || 0) + ((d.xp || 0) * 0.001) + ((d.yearTrips || 0) * 0.05) + ((d.satisfaction || 0) * 0.02),
  }));
  const ranked = [...scoredDrivers].sort((a, b) => b.score - a.score);
  const topDrivers = ranked.slice(0, 3);
  const bottomDrivers = ranked.length > 3 ? ranked.slice(-3).reverse() : [];

  // Auto-bonus for the top 3: +5 satisfaction (recognition). Applied
  // BEFORE the final commit so the next year starts fresh.
  const topIds = new Set(topDrivers.map(d => d.id));
  if (topIds.size > 0) {
    // Patch driversAfterLease in place — these drivers were already
    // captured for the year close; the satisfaction tick reflects in
    // the next month onward.
    for (let i = 0; i < driversAfterLease.length; i++) {
      if (topIds.has(driversAfterLease[i].id)) {
        driversAfterLease[i] = {
          ...driversAfterLease[i],
          satisfaction: Math.min(100, (driversAfterLease[i].satisfaction || 0) + 5),
        };
      }
    }
  }

  return {
    year: closingYear,
    months: yearMonths,
    totalRevenue: Math.round(totalRevenue),
    totalExpenses: Math.round(totalExpenses),
    netResult: Math.round(netResult),
    treasuryStart: Math.round(firstTreasury),
    treasuryEnd: Math.round(finalTreasury),
    treasuryDelta,
    bestMonth: { month: bestMonth.month, netResult: bestMonth.netResult },
    worstMonth: { month: worstMonth.month, netResult: worstMonth.netResult },
    vehiclesTotal: finalVehicles.length,
    driversTotal: driversAfterLease.length,
    reputation: Math.round(state.reputation),
    notoriety: Math.round(state.notoriety || 0),
    contractsCompleted: yearMonths.reduce((s, m) => s + (m.contractsCompleted || 0), 0),
    achievementsCount: Object.keys(state.achievements || {}).length,
    topDrivers,
    bottomDrivers,
    // ── RH KPIs (extension 23/02/2026) ─────────────────────────────
    // Surfaces in AnnualBoardMeeting: climat social, burnout retention,
    // worst assignments. Built from a snapshot of state at year close.
    rhReport: computeAnnualReport({
      ...state,
      drivers: driversAfterLease,
      vehicles: finalVehicles,
    }, closingYear),
    // 02/06/2026 — Tender / AO yearly recap. Null when no AO activity
    // AND no pending applications happened during the closing year.
    tenderStats: buildYearTenderStats(state, closingYear),
    // 02/06/2026 — Coût RH évitable : somme des indemnités de rupture
    // versées pendant l'exercice (rupture conv., licenciement, fin de
    // période d'essai). KPI pédagogique : la fidélisation est un levier.
    avoidableHrCost: buildAvoidableHrCost(state, closingYear),
    // 22/07/2026 — Contrats honorés vs laissés filer par tranche de valeur
    // (retour user, comité de direction) + panier moyen réel des livrés.
    contractDeals: buildContractAcceptanceStats(state, state.totalDays + 1),
    avgContractValue: computeAvgContractValue(state, state.totalDays + 1),
    // 22/07/2026 — Rapport Flotte annuel (usage & rentabilité par camion),
    // valeur ajoutée de Rémi Forni : null si non recruté.
    fleetReport: state.hiredSupport?.['role-christophe-faure']
      ? buildFleetYearReport(finalVehicles, state.activeContracts)
      : null,
    // 25/07/2026 (demande user) — Débrief chronologique de fin d'année :
    // frise des points de passage + comparaison inter-joueurs (télémétrie).
    yearDebrief: buildYearDebrief({
      state, closingYear, yearMonths, finalTreasury, finalVehicles, driversAfterLease,
    }),
  };
}

/**
 * Sum the `total` of every severanceLedger entry whose `year` matches the
 * closing year, plus a small breakdown by mode for UI display.
 *
 * @returns {{ total: number, count: number, byMode: object, entries: Array }}
 */
function buildAvoidableHrCost(state, closingYear) {
  const ledger = (state.severanceLedger || []).filter(e => e.year === closingYear);
  let total = 0;
  const byMode = { rupture: 0, fault: 0, probation: 0 };
  for (const e of ledger) {
    total += e.total || 0;
    if (typeof byMode[e.mode] === 'number') byMode[e.mode] += e.total || 0;
  }
  return {
    total: Math.round(total),
    count: ledger.length,
    byMode,
    entries: ledger.map(e => ({
      day: e.day,
      driverName: e.driverName,
      mode: e.mode,
      total: e.total,
    })),
  };
}
